import _ from 'lodash';

import { M4, V4 } from '@moleculeone/utils';
import { addBond, mergeMol, removeAtom, setUnchangedPositions } from './methods';
import { atomToNeighborsIndices, atomToNeighborsPairs } from './selectors';
import { alignMatrix } from './positions/mergeGroupTrees';
import { overlapScore } from './positions/utils';
import { align } from './geometry';

function prepareFragment(target, targetAtomIndexes, fragment, fragmentAtomIndexes) {
  if (Object.keys(fragment.atoms).length === 1) {
    return fragment;
  }
  const targetAtoms = targetAtomIndexes.map(idx => target.atoms[idx]);
  const fragmentAtoms = fragmentAtomIndexes.map(idx => fragment.atoms[idx]);

  const newFragment = _.cloneDeep(fragment);

  fragmentAtoms.forEach((atom, index) => {
    if (atom.element === 'C' && targetAtoms[index].element !== 'C') {
      newFragment.atoms[atom.idx] = targetAtoms[index];
    }
  });

  return newFragment;
}

export function mergeOnBond(target, targetBondIdx, fragment, fragmentBondIdx, shouldSwap) {
  const targetStart = target.bonds[targetBondIdx].start;
  const targetEnd = target.bonds[targetBondIdx].end;
  let fragmentStart = fragment.bonds[fragmentBondIdx].start;
  let fragmentEnd = fragment.bonds[fragmentBondIdx].end;

  if (shouldSwap) {
    [fragmentStart, fragmentEnd] = [fragmentEnd, fragmentStart];
  }

  let { newMol, newAtomIdx } = mergeMol(
    target,
    prepareFragment(target, [targetStart, targetEnd], fragment, [fragmentStart, fragmentEnd]),
    false
  );
  const [newStart, newEnd] = [newAtomIdx(fragmentStart), newAtomIdx(fragmentEnd)];
  const neighbors = atomToNeighborsPairs(newMol);

  // replace target-target bonds with target-fragment bonds
  for (const [bond] of neighbors[targetStart]) {
    if (bond.idx !== targetBondIdx) {
      [newMol] = addBond(
        newMol,
        bond.start === targetStart ? newStart : bond.start,
        bond.end === targetStart ? newStart : bond.end,
        bond.order,
        bond.type,
        null
      );
    }
  }

  for (const [bond] of neighbors[targetEnd]) {
    if (bond.idx !== targetBondIdx) {
      [newMol] = addBond(
        newMol,
        bond.start === targetEnd ? newEnd : bond.start,
        bond.end === targetEnd ? newEnd : bond.end,
        bond.order,
        bond.type,
        null
      );
    }
  }

  newMol = removeAtom(newMol, targetStart);
  newMol = removeAtom(newMol, targetEnd);

  const gTarget = { ...target.positions };
  gTarget[newStart] = gTarget[targetStart];
  gTarget[newEnd] = gTarget[targetEnd];
  delete gTarget[targetStart];
  delete gTarget[targetEnd];

  const gFragment1 = {};
  for (const [idx, pos] of Object.entries(fragment.positions)) {
    gFragment1[newAtomIdx(idx)] = pos;
  }
  const gFragment2 = M4.map(M4.flipX, gFragment1);

  const align1 = align(gFragment1, gTarget);
  const align2 = align(gFragment2, gTarget);
  const score1 = overlapScore(newMol, gTarget, align1);
  const score2 = overlapScore(newMol, gTarget, align2);
  const best = score1 > score2 ? align1 : align2;

  return setUnchangedPositions(newMol, { ...newMol.positions, ...best });
}

export function mergeOnPivot(target, targetIdx, fragment, fragmentIdx) {
  let { newMol, newAtomIdx } = mergeMol(target, prepareFragment(target, [targetIdx], fragment, [fragmentIdx]), false);
  const bondArrs = atomToNeighborsIndices(newMol);

  let neighbors = atomToNeighborsPairs(newMol);
  const pivotIdx = newAtomIdx(fragmentIdx);
  for (const [bond] of neighbors[targetIdx]) {
    [newMol] = addBond(
      newMol,
      bond.start === targetIdx ? pivotIdx : bond.start,
      bond.end === targetIdx ? pivotIdx : bond.end,
      bond.order,
      bond.type,
      null
    );
  }
  newMol = removeAtom(newMol, targetIdx);
  neighbors = atomToNeighborsPairs(newMol);

  const mergedTargetPositions = { ...target.positions };
  mergedTargetPositions[pivotIdx] = mergedTargetPositions[targetIdx];
  delete mergedTargetPositions[targetIdx]; // this atom will be replaced with fragment's pivot

  const mergedFragmentPositions = {};
  for (const [idx, pos] of Object.entries(fragment.positions)) {
    mergedFragmentPositions[newAtomIdx(idx)] = pos;
  }

  const fragmentReferencePoints = [];
  const targetReferencePoints = [];
  for (const [, atom] of neighbors[pivotIdx]) {
    if (mergedFragmentPositions[atom.idx] != null) {
      fragmentReferencePoints.push(mergedFragmentPositions[atom.idx]);
    } else {
      targetReferencePoints.push(mergedTargetPositions[atom.idx]);
    }
  }

  const fanSize = Math.max(neighbors[pivotIdx].length, 3);

  let bestScore = 0;
  let bestFragmentPositions = null;

  const targetPivot = mergedTargetPositions[pivotIdx];
  const fragPivot = mergedFragmentPositions[pivotIdx];
  for (let i = 1; i < fanSize; i++) {
    const angle = (i * Math.PI * 2.0) / fanSize;
    for (const flip of [true, false]) {
      for (const targetRef of targetReferencePoints) {
        for (const fragRef of fragmentReferencePoints) {
          const matrix = alignMatrix(targetPivot, targetRef, fragPivot, fragRef, angle, flip);
          const alignedFragPositions = M4.map(matrix, mergedFragmentPositions);
          const score = overlapScore(newMol, mergedTargetPositions, alignedFragPositions, bondArrs);
          if (score > bestScore) {
            bestScore = score;
            bestFragmentPositions = alignedFragPositions;
          }
        }
      }
    }
  }

  if (bestFragmentPositions === null) {
    const translate = M4.mulM(M4.moveTo(targetPivot), M4.moveFrom(fragPivot));

    bestFragmentPositions = M4.map(translate, mergedFragmentPositions);
  }

  newMol.positions = { ...newMol.positions, ...bestFragmentPositions };
  return setUnchangedPositions(newMol, { ...newMol.positions, ...bestFragmentPositions });
}

export function createOnEmpty(target, fragment, fragmentIdxList, position) {
  const avgFragmentPosition = V4.average(...fragmentIdxList.map(idx => fragment.positions[idx]));

  const translate = M4.mulM(M4.moveTo(position), M4.moveFrom(avgFragmentPosition));

  const newFragmentPositions = M4.map(translate, fragment.positions);
  const movedFragment = setUnchangedPositions(fragment, newFragmentPositions);

  const { newMol } = mergeMol(target, movedFragment, false);
  return newMol;
}
